import {
  IconChevronDown, IconSettings, IconBuildingBank, IconBox, IconChartArcs,
  IconHeadset, IconClipboardCheck,
} from '@tabler/icons-react';
import { Avatar, Box, Center, Group, HoverCard, Indicator, Menu, SimpleGrid, Text, Tooltip, ActionIcon, UnstyledButton } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../images/Logo_Black_Smooth.png';
import classes from './Header.module.css';

export function Header({ user, isAuthenticated, userRoles, hasNotStarted, hasPendingReviews, userIsRiskManager, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const assetsLinks = [
    { title: 'AI Model Inventory', description: 'View and manage AI models', path: '/model-inventory' },
  ].map((item) => (
    <UnstyledButton className={classes.subLink} key={item.title} onClick={() => navigate(item.path)}>
      <div>
        <Text size="sm" fw={500}>{item.title}</Text>
        <Text size="xs" c="dimmed">{item.description}</Text>
      </div>
    </UnstyledButton>
  ));

  const isAssetsActive = location.pathname.startsWith('/model-inventory');

  const landscapeLinks = [
    { title: 'Risk Landscape', description: 'Aggregate Huginn dashboard — by source, priority, residual, skill', path: '/landscape' },
    { title: 'Reports', description: 'Audit-ready report canvas per Huginn assessment', path: '/reports' },
  ].map((item) => (
    <UnstyledButton className={classes.subLink} key={item.title} onClick={() => navigate(item.path)}>
      <div>
        <Text size="sm" fw={500}>{item.title}</Text>
        <Text size="xs" c="dimmed">{item.description}</Text>
      </div>
    </UnstyledButton>
  ));

  const isLandscapeActive =
    location.pathname === '/landscape' ||
    location.pathname === '/reports' ||
    location.pathname === '/risk-reports' ||
    location.pathname === '/legacy-landscape' ||
    location.pathname === '/legacy-reports';

  // Team Members get a simplified nav
  const isManager = userIsRiskManager;

  return (
    <header className={classes.header}>
      <div className={classes.content}>
        {/* Left edge: logo */}
        <button className={classes.edgeButton} aria-label="Home" onClick={() => navigate('/')}>
          <img src={Logo} alt="logo" className={classes.logo} />
        </button>

        {/* Middle: nav */}
        <Group h="100%" gap={0}>
          {isManager ? (
            /* Risk Manager nav — full access */
            <>
              <button className={classes.link} data-active={location.pathname === '/' || undefined} onClick={() => navigate('/')}>
                <Center inline>
                  <IconHeadset size={16} style={{ marginRight: 4 }} />
                  <Box component="span">Service Desk</Box>
                </Center>
              </button>

              <button
                className={classes.link}
                data-active={location.pathname.startsWith('/agency') || undefined}
                onClick={() => navigate('/agency')}
              >
                <Center inline>
                  <IconBuildingBank size={16} style={{ marginRight: 4 }} />
                  <Box component="span">Agency</Box>
                </Center>
              </button>

              <Indicator
                disabled={!hasNotStarted && !hasPendingReviews}
                size={8}
                position="top-end"
                offset={5}
                inline
                processing
                color={hasPendingReviews ? "orange" : "red"}
              >
                <button
                  className={classes.link}
                  data-active={location.pathname === '/assessments' || location.pathname === '/risk-assessments' || undefined}
                  onClick={() => navigate('/assessments')}
                >
                  <Center inline>
                    <IconClipboardCheck size={16} style={{ marginRight: 4 }} />
                    <Box component="span">Assessments</Box>
                  </Center>
                </button>
              </Indicator>

              <HoverCard width={260} position="bottom" radius="md" shadow="md" withinPortal>
                <HoverCard.Target>
                  <button className={classes.link} data-active={isLandscapeActive || undefined}>
                    <Center inline>
                      <IconChartArcs size={16} style={{ marginRight: 4 }} />
                      <Box component="span" mr={5}>Landscape</Box>
                      <IconChevronDown size={16} />
                    </Center>
                  </button>
                </HoverCard.Target>
                <HoverCard.Dropdown style={{ overflow: 'hidden' }}>
                  <SimpleGrid cols={1} spacing={0}>{landscapeLinks}</SimpleGrid>
                </HoverCard.Dropdown>
              </HoverCard>

              <HoverCard width={260} position="bottom" radius="md" shadow="md" withinPortal>
                <HoverCard.Target>
                  <button className={classes.link} data-active={isAssetsActive || undefined}>
                    <Center inline>
                      <IconBox size={16} style={{ marginRight: 4 }} />
                      <Box component="span" mr={5}>Assets & Configurations</Box>
                      <IconChevronDown size={16} />
                    </Center>
                  </button>
                </HoverCard.Target>
                <HoverCard.Dropdown style={{ overflow: 'hidden' }}>
                  <SimpleGrid cols={1} spacing={0}>{assetsLinks}</SimpleGrid>
                </HoverCard.Dropdown>
              </HoverCard>
            </>
          ) : (
            /* Team Member nav — simplified */
            <>
              <button
                className={classes.link}
                data-active={location.pathname === '/' || undefined}
                onClick={() => navigate('/')}
              >
                Dashboard
              </button>

              <button
                className={classes.link}
                data-active={location.pathname === '/assessments' || location.pathname.startsWith('/assessments/') || undefined}
                onClick={() => navigate('/assessments')}
              >
                Assessments
              </button>

              <button
                className={classes.link}
                data-active={location.pathname === '/reports' || undefined}
                onClick={() => navigate('/reports')}
              >
                Reports
              </button>
            </>
          )}
        </Group>

        {/* Right edge: avatar menu */}
        {isAuthenticated && user && (
          <Menu>
            <Menu.Target>
              <Tooltip label={`Role: ${userRoles.join(', ') || 'User'}`}>
                <ActionIcon variant="transparent" radius="xl" size={44} className={classes.edgeIcon}>
                  <Avatar src={user.picture} alt={user.name || 'User'} radius="xl" size={44} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item disabled>Hello, {user?.name || 'User'}</Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconSettings size={16} />} disabled>
                Settings
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={onLogout}>Logout</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
    </header>
  );
}
